import { useMutation } from "@apollo/client";
import { MUTATION_EXECUTE_QUICK_WIN } from "../../../../utilities/llm-quick-wins";
import {
  ErrorCode,
  ExecuteLlmQuickWinResult,
} from "@towersystems/roam-common/lib/generated-types";
import { NameSuggestion, ScreenContext } from "./common";
import React from "react";
import {
  Button,
  FlatActionList,
  FlatActionListItem,
  Page,
  useFlashMessages,
  Text,
} from "@towersystems/roam-common-native";
import { StackScreenProps } from "@react-navigation/stack";
import { ProductSeoFriendlyNameLlmQuickWinRootParamList } from "../../../../types";
import { useHasTransitionEnded } from "../../../../utilities/use-after-interactions";
import { FlatListSearchableSkeleton } from "../../../../components/FlatListSearchableSkeleton";
import { Colors, View } from "react-native-ui-lib";
import { Feather, Ionicons } from "@expo/vector-icons";
import { useTranslation } from "../../../../utilities/i18n";

export interface ProductSeoFriendlyNameReviewScreenProps
  extends StackScreenProps<
    ProductSeoFriendlyNameLlmQuickWinRootParamList,
    "ProductSeoFriendlyNameReviewScreen"
  > {}

export const ProductSeoFriendlyNameReviewScreen = ({
  navigation,
}: ProductSeoFriendlyNameReviewScreenProps) => {
  const [selectedIndex, setSelectedIndex] = React.useState<number | null>(null);

  const screenContext = React.useContext(ScreenContext);
  const { onSubmit, originalFormValues } = screenContext;

  const [generatedNameSuggestions, setGeneratedNameSuggestions] =
    React.useState<NameSuggestion[] | undefined>(undefined);

  const flashMessagesContext = useFlashMessages();
  const transitionEnded = useHasTransitionEnded(navigation);
  const { t } = useTranslation("product");

  const [m] = useMutation<{
    executeLlmQuickWin: ExecuteLlmQuickWinResult;
  }>(MUTATION_EXECUTE_QUICK_WIN);

  React.useEffect(() => {
    (async () => {
      return handleOnGenerate();
    })();
  }, []);

  const handleOnGenerate = async () => {
    setGeneratedNameSuggestions(undefined);
    setSelectedIndex(null);
    return m({
      variables: {
        input: {
          product_seo_friendly_name_quick_win: {
            productName: originalFormValues.name || "",
            productDescription: originalFormValues.description || "",
          },
        },
      },
    })
      .then((response) => {
        const result = response.data?.executeLlmQuickWin;
        if (result && "result" in result) {
          console.log("suggestions result:", result.result);
          const parsed = JSON.parse(result.result.replace(/\n/g, ""));
          setGeneratedNameSuggestions(parsed.nameSuggestions);
        } else {
          if (result && "message" in result) {
            throw new Error(result.message);
          }
          flashMessagesContext.showMessage({
            message: "Failed to generate suggestions. Check your credentials.",
            severity: "error",
          });
        }
      })
      .catch((error) => {
        const msg = error?.message ?? "Failed to generate suggestions";

        flashMessagesContext.showMessage({
          message: msg,
          severity: "error",
        });

        if (
          [
            ErrorCode.INVALID_LLM_CREDENTIALS_ERROR,
            ErrorCode.MISSING_LLM_STRATEGY_ERROR,
          ].includes(msg)
        ) {
          navigation.goBack();
        }
      });
  };

  if (generatedNameSuggestions === undefined || !transitionEnded) {
    return <FlatListSearchableSkeleton searchbar={false} />;
  }

  const items: FlatActionListItem[] = generatedNameSuggestions.map(
    (suggestion, index) => ({
      key: `generated-tag-${index}`,
      title: suggestion.name,
      onPress: () => setSelectedIndex(index),
      selected: selectedIndex === index,

      contentStyles: {
        backgroundColor: selectedIndex === index ? "#ebebeb" : undefined,
        padding: 12,
        borderRadius: 8,
      },
      componentRight: (icon) => {
        return (
          <View row centerV style={{ marginLeft: 20 }}>
            <View style={{ marginLeft: 16 }}>
              {selectedIndex === index ? (
                <Feather name="check" size={24} color={Colors.action} />
              ) : (
                <Feather
                  name="check"
                  size={24}
                  color={Colors.action}
                  style={{
                    opacity: 0,
                  }}
                />
              )}
            </View>
          </View>
        );
      },
      titleContent({ title, subTitle }) {
        return (
          <View>
            {title}
            {subTitle}
            <View style={{ marginTop: 8 }}>
              <Text variant="body" color={Colors.subdued}>
                {suggestion.reasoning}
              </Text>
            </View>
          </View>
        );
      },
    })
  );

  const disabled =
    generatedNameSuggestions === undefined ||
    generatedNameSuggestions.length === 0;

  const footerMarkup = (
    <View row>
      <View marginR-10 flex>
        <Button
          disabled={disabled}
          label={t("button.generate_product_name")}
          isAction
          onPress={() => handleOnGenerate()}
          icon={<Ionicons name="sparkles" size={17} color={Colors.action} />}
        ></Button>
      </View>
      <View flex>
        <Button
          disabled={
            generatedNameSuggestions === undefined ||
            generatedNameSuggestions.length === 0 ||
            selectedIndex === null
          }
          label={t("button.add_generated_product_name")}
          backgroundColor={Colors.action}
          color="white"
          onPress={() => {
            if (selectedIndex !== null) {
              const selectedSuggestion =
                generatedNameSuggestions[selectedIndex];
              if (selectedSuggestion) {
                onSubmit({ name: selectedSuggestion.name });
              }
            }
          }}
        ></Button>
      </View>
    </View>
  );

  return (
    <Page
      title={t("title.generated_product_name")}
      secondaryTitle={t("title.generated_product_name_secondary")}
      footer={footerMarkup}
    >
      <View>
        <FlatActionList items={items} />
      </View>
    </Page>
  );
};
